import { useEffect, useState } from 'react';
import { fetchInvoiceSummaryApi } from '~/api';
import { useSelector } from '~/store';
import { selectBusinessUuid, selectUser } from '~/store/slices/formSlice/selectors';
import { ApiResponseEnum, apiResponseHandler } from '~/utils/api';

interface InvoiceCardData {
  title: string;
  subtitle?: string;
  amount: number;
  count: number;
  color?: string;
  currency: string;
}

type ApiInvoiceSummary = Record<
  'draft' | 'paid' | 'outstanding' | 'overdue',
  { amount: string; count: string; currency: string }
>;

const defaultCurrency = 'USD';

const initialCardData: Record<'draft' | 'paid' | 'outstanding' | 'overdue', InvoiceCardData> = {
  draft: {
    title: 'Draft Invoices',
    subtitle: 'All Time',
    amount: 0,
    count: 0,
    color: undefined,
    currency: defaultCurrency,
  },
  paid: {
    title: 'Paid Invoices',
    subtitle: 'This Month',
    amount: 0,
    count: 0,
    color: '#2e7d32',
    currency: defaultCurrency,
  },
  outstanding: {
    title: 'Outstanding Invoices',
    subtitle: 'All Time',
    amount: 0,
    count: 0,
    color: '#1565c0',
    currency: defaultCurrency,
  },
  overdue: {
    title: 'Overdue Invoices',
    subtitle: undefined,
    amount: 0,
    count: 0,
    color: '#c62828',
    currency: defaultCurrency,
  },
};

export const useInvoiceSummary = () => {
  const user = useSelector(selectUser);
  const currency = user.businessDetails.currency ?? defaultCurrency;
  const businessUuid = useSelector(selectBusinessUuid);

  const [data, setData] = useState<typeof initialCardData>(initialCardData);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const fetchInvoiceSummary = async () => {
      setLoading(true);
      const promise = await fetchInvoiceSummaryApi(businessUuid, currency);
      const response = await apiResponseHandler<ApiInvoiceSummary>(promise);

      if (isMounted) {
        if (response.type === ApiResponseEnum.Success) {
          const apiData = response.data;
          const updatedData: typeof initialCardData = {
            draft: {
              ...initialCardData.draft,
              amount: parseFloat(apiData.draft.amount),
              count: parseInt(apiData.draft.count, 10),
              currency: apiData.draft.currency,
            },
            paid: {
              ...initialCardData.paid,
              amount: parseFloat(apiData.paid.amount),
              count: parseInt(apiData.paid.count, 10),
              currency: apiData.paid.currency,
            },
            outstanding: {
              ...initialCardData.outstanding,
              amount: parseFloat(apiData.outstanding.amount),
              count: parseInt(apiData.outstanding.count, 10),
              currency: apiData.outstanding.currency,
            },
            overdue: {
              ...initialCardData.overdue,
              amount: parseFloat(apiData.overdue.amount),
              count: parseInt(apiData.overdue.count, 10),
              currency: apiData.overdue.currency,
            },
          };
          setData(updatedData);
        } else {
          setData(initialCardData);
        }
        setLoading(false);
      }
    };

    fetchInvoiceSummary();

    return () => {
      isMounted = false;
    };
  }, [currency]);

  return { data, loading, currency };
};
